/**
 * Cmd/Ctrl+S across the admin and the preview iframe.
 *
 * The browser "Save page" dialog is never useful here, so it is blocked on
 * every admin window. Keystrokes inside the preview iframe never reach the
 * admin document, so the iframe forwards them with postMessage and the admin
 * re-broadcasts one `SAVE_SHORTCUT_EVENT` that SaveCancelToast listens to.
 * Capture phase on window so editors that stop propagation cannot swallow it.
 */

export const SAVE_SHORTCUT_EVENT = 'portfolio:save-shortcut';
export const SAVE_SHORTCUT_MESSAGE = 'portfolio:save-shortcut';

/** @param {KeyboardEvent} event */
export function isSaveShortcut(event) {
    if (!event || !(event.metaKey || event.ctrlKey) || event.altKey) return false;
    return event.code === 'KeyS' || String(event.key || '').toLowerCase() === 's';
}

function broadcast(win) {
    win.dispatchEvent(new CustomEvent(SAVE_SHORTCUT_EVENT));
}

/**
 * Admin window: block the browser dialog and broadcast saves, including ones
 * forwarded from same-origin iframes. Idempotent.
 * @param {Window} [win]
 * @returns {() => void} uninstall
 */
export function installAdminSaveShortcut(win = typeof window !== 'undefined' ? window : null) {
    if (!win || win.__saveShortcutInstalled) return () => {};
    win.__saveShortcutInstalled = true;

    const onKeyDown = (event) => {
        if (!isSaveShortcut(event)) return;
        event.preventDefault();
        if (event.repeat) return;
        broadcast(win);
    };
    const onMessage = (event) => {
        if (event.origin !== win.location.origin) return;
        if (event.data?.type !== SAVE_SHORTCUT_MESSAGE) return;
        broadcast(win);
    };

    win.addEventListener('keydown', onKeyDown, true);
    win.addEventListener('message', onMessage);
    return () => {
        win.removeEventListener('keydown', onKeyDown, true);
        win.removeEventListener('message', onMessage);
        win.__saveShortcutInstalled = false;
    };
}

/**
 * Preview iframe: block the browser dialog and hand the shortcut to the admin.
 * Idempotent.
 * @param {Window} [win]
 * @returns {() => void} uninstall
 */
export function installFrameSaveShortcut(win = typeof window !== 'undefined' ? window : null) {
    if (!win || win.parent === win || win.__saveShortcutInstalled) return () => {};
    win.__saveShortcutInstalled = true;

    const onKeyDown = (event) => {
        if (!isSaveShortcut(event)) return;
        event.preventDefault();
        if (event.repeat) return;
        win.parent.postMessage({ type: SAVE_SHORTCUT_MESSAGE }, win.location.origin);
    };

    win.addEventListener('keydown', onKeyDown, true);
    return () => {
        win.removeEventListener('keydown', onKeyDown, true);
        win.__saveShortcutInstalled = false;
    };
}
